import parse from 'html-react-parser';
import React from 'react';

import LabelValueAttribute from '@/components/catalog-attributes/LabelValueAttribute';
import { Image } from '@/components/core/Image';
import LabelValue from '@/components/core/LabelValue/LabelValue';
import { useAttributeLabelTranslator } from '@/components/datamodel/hooks/useAttributeLabelTranslator';
import { useOptionTranslator } from '@/components/datamodel/hooks/useOptionTranslator';
import { useOrganization } from '@/hooks/useOrganization';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { cn, restUrl } from '@/services/utils';
import { DataModelAttribute, DataModelAttributeTypeName, PersistedItem } from '@/types/DataModel';

import { useSecondaryItemTitle } from '../hooks/useSecondaryItemTitle';

interface ResolvedAttributeConfig {
    attribute: DataModelAttribute;
    valuesFormat: ('icon' | 'label' | 'key')[];
}

interface ItemDialogHeaderSecondaryProps {
    item?: PersistedItem;
    className?: string;
}

// The sub-row's container styling mirrors the Figma "ItemEditor/HeaderSecondary"
// frame (node 4299:5170): fill-tertiary background, bottom divider, 32px/12px
// padding on lg, flex-wrap with 16px/24px column gap (breakpoint-scaled) and
// 12px row gap, min-height 48px on lg (matching the Figma 48px band; the row
// grows on wrap at narrower breakpoints, matching the sm/xs frames).
const containerClassName = cn(
    'tw-flex tw-flex-wrap tw-items-center',
    'tw-gap-x-4 lg:tw-gap-x-6 tw-gap-y-3',
    'tw-py-3 tw-px-5 lg:tw-px-8 lg:tw-min-h-12',
    'tw-bg-fill-tertiary',
    'tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-b tw-border-solid tw-border-tertiary',
);

const ENUM_TYPES: (DataModelAttributeTypeName | undefined)[] = [
    DataModelAttributeTypeName.Enum,
    DataModelAttributeTypeName.EnumSet,
    DataModelAttributeTypeName.OpenEnum,
    DataModelAttributeTypeName.OpenEnumSet,
];

const EnumAttributeValue: React.FC<{
    attribute: DataModelAttribute;
    item: PersistedItem;
    valuesFormat: ('icon' | 'label' | 'key')[];
}> = ({ attribute, item, valuesFormat }) => {
    const { translateAttributeLabel } = useAttributeLabelTranslator();
    const { translateOption } = useOptionTranslator();
    const { organization } = useOrganization();

    const hasIcon = valuesFormat.includes('icon');
    const hasLabel = valuesFormat.includes('label');
    const hasKey = valuesFormat.includes('key');

    // At least the value (`label`) or the `key` must be requested — an
    // icon-only enum row would render a stray label with no companion text
    // (see Anna's UX review on LAX-9944). Warn once and skip rendering.
    if (!hasLabel && !hasKey) {
        logger.warn(
            `Secondary item title: enum attribute '${attribute.name}' has ` +
            `valuesFormat=[${valuesFormat.join(', ')}] which lacks both 'label' and 'key'. ` +
            'At least one of them must be present; skipping this attribute.',
        );
        return null;
    }

    const attributeData = item[attribute.name];
    const label = translateAttributeLabel(attribute);

    if (attributeData === undefined || attributeData === null || attributeData === '') {
        return <LabelValue label={label} value="—" />;
    }

    const optionKey = String(attributeData);
    const translatedLabel = hasLabel ? translateOption(optionKey, attribute, attribute.options) : '';

    let iconElement: React.ReactNode = null;
    if (hasIcon) {
        const $angularJSInjector = getInjector();
        const $rootScope = getRootScope($angularJSInjector);
        const iconUrl = $rootScope.translateOptionIcon?.(optionKey, attribute);
        if (iconUrl && organization?.dataModelGatheringKey) {
            const fullUrl = restUrl(`gatherings/${organization.dataModelGatheringKey}/${iconUrl}`);
            iconElement = (
                <Image
                    className="tw-h-full tw-w-full tw-object-contain"
                    alt={optionKey}
                    src={fullUrl}
                />
            );
        }
    }

    // The value slot is only populated when `label` is requested. Falling back
    // to `optionKey` here would duplicate the key suffix (`[:icon, :key]`) and
    // resurrect unwanted text in icon-only configs.
    const value = hasLabel ? (translatedLabel || optionKey) : undefined;
    const keySuffix = hasKey ? optionKey : undefined;

    // `LabelValue`'s discriminated props require at least one of `value` /
    // `keySuffix`; the early return above guarantees that invariant.
    return (
        <LabelValue
            label={label}
            icon={iconElement}
            {...(value !== undefined
                ? { value, keySuffix }
                : { keySuffix: keySuffix as string })}
        />
    );
};

const AttributeItem: React.FC<{
    config: ResolvedAttributeConfig;
    item: PersistedItem;
}> = ({ config, item }) => {
    const { attribute, valuesFormat } = config;

    const isEnumType = ENUM_TYPES.includes(attribute.typeName);

    if (isEnumType && valuesFormat.length > 0) {
        return <EnumAttributeValue attribute={attribute} item={item} valuesFormat={valuesFormat} />;
    }

    return <LabelValueAttribute attribute={attribute} item={item} />;
};

interface ItemDialogHeaderSecondaryViewProps {
    /** Renders raw HTML from the legacy `filter`-mode of `useSecondaryItemTitle`. */
    filterHtml?: string;
    /** Attribute configs resolved from the `attributes`-mode of `useSecondaryItemTitle`. */
    attributeConfigs?: ResolvedAttributeConfig[];
    /** Item to source attribute values from. Required when `attributeConfigs` is provided. */
    item?: PersistedItem;
    className?: string;
}

/**
 * Presentational body of the secondary sub-row. Split out from
 * `ItemDialogHeaderSecondary` so Storybook (and other callers that already
 * have resolved configs) can render the row without needing the full
 * `LayoutProvider` + data-model wiring that `useSecondaryItemTitle` requires.
 */
const ItemDialogHeaderSecondaryView: React.FC<ItemDialogHeaderSecondaryViewProps> = ({
    filterHtml,
    attributeConfigs,
    item,
    className,
}) => {
    if (filterHtml) {
        return (
            <div className={cn(containerClassName, className)}>
                {parse(filterHtml)}
            </div>
        );
    }

    if (attributeConfigs && attributeConfigs.length > 0 && item) {
        return (
            <div className={cn(containerClassName, className)}>
                {attributeConfigs.map((config) => (
                    <AttributeItem key={config.attribute.name} config={config} item={item} />
                ))}
            </div>
        );
    }

    return null;
};

/**
 * Secondary sub-row of the item editor dialog header (Figma:
 * `ItemEditor/HeaderSecondary`, node 4299:4878). Sits directly under the main
 * `DialogHeader` row and renders a wrap-friendly row of identifying attributes
 * configured on the layout via `secondary_item_title_filter`.
 *
 * The row is *not* wired to the accordion's currently-open section: per the
 * Figma "in use" frames (node 4332:15603) it displays a fixed set of layout-
 * configured identifying attributes (e.g. Target Market / GTIN / Packaging
 * Unit) that stay visible regardless of which section is expanded below.
 */
const ItemDialogHeaderSecondary: React.FC<ItemDialogHeaderSecondaryProps> = ({ item, className }) => {
    const { mode, filterHtml, attributeConfigs } = useSecondaryItemTitle({ item });

    if (mode === 'none') {
        return null;
    }

    if (mode === 'filter') {
        return (
            <ItemDialogHeaderSecondaryView filterHtml={filterHtml} className={className} />
        );
    }

    if (mode === 'attributes') {
        return (
            <ItemDialogHeaderSecondaryView
                attributeConfigs={attributeConfigs}
                item={item}
                className={className}
            />
        );
    }

    return null;
};

export { ItemDialogHeaderSecondary, ItemDialogHeaderSecondaryView };
export type { ItemDialogHeaderSecondaryViewProps, ResolvedAttributeConfig };
